/**
 * HomeScreen - Premium discover screen matching v0 design
 * Layout: Title → Search → Category pills → Sort → Agent cards
 */

import { useCallback, useEffect, useState } from 'react';
import type { Agent, AgentCategory, Collection } from '../types/agent';
import { AgentList } from '../components/AgentList';
import { SearchBar } from '../components/SearchBar';
import { HomeSkeleton } from '../components/Skeleton';
import { hapticFeedback } from '../lib/telegram';
import { getAgents, getCollections } from '../lib/api';
import { CATEGORIES } from '../types/agent';

interface HomeScreenProps {
  onAgentClick: (agentId: string) => void;
  onSubmitClick: () => void;
  onCollectionClick?: (slug: string) => void;
}

/** Featured hero card — single prominent featured app */
function FeaturedHero({ agent, onClick }: { agent: Agent; onClick: (id: string) => void }) {
  return (
    <div
      onClick={() => { hapticFeedback('light'); onClick(agent.id); }}
      className="relative bg-[#242424] border border-white/5 rounded-xl overflow-hidden cursor-pointer group active:scale-[0.98] transition-transform duration-200 focus-visible:ring-2 focus-visible:ring-[#0098EA] outline-none"
      tabIndex={0}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#0098EA]/20 via-[#0098EA]/5 to-transparent" />
      <div className="relative p-5 flex gap-4 items-center">
        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#1a1a1a] flex items-center justify-center overflow-hidden shadow-lg ring-2 ring-[#0098EA]/30">
          {agent.logo ? (
            <img src={agent.logo} alt={agent.name} className="w-full h-full object-cover" onError={(e) => {
              const t = e.target as HTMLImageElement; t.style.display = 'none'; t.parentElement!.innerHTML = '<span class="text-3xl">🤖</span>';
            }} />
          ) : <span className="text-3xl">🤖</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[11px] font-bold text-[#0098EA] bg-[#0098EA]/15 px-2 py-0.5 rounded-full">⭐ Featured</span>
          </div>
          <h2 className="text-lg font-bold text-white truncate">{agent.name}</h2>
          <p className="text-sm text-[#a0a0a0] line-clamp-2 mt-0.5">{agent.description}</p>
          {agent.review_count !== undefined && agent.review_count > 0 && (
            <span className="text-xs text-yellow-400 mt-1 inline-block">⭐ {(agent.avg_rating || 0).toFixed(1)} ({agent.review_count})</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Collection gradient mapping */
const COLLECTION_GRADIENTS = [
  'from-[#0098EA]/20 to-[#06B6D4]/10',
  'from-[#A855F7]/20 to-[#EC4899]/10',
  'from-[#22C55E]/20 to-[#EAB308]/10',
  'from-[#F97316]/20 to-[#EF4444]/10',
  'from-[#06B6D4]/20 to-[#0098EA]/10',
  'from-[#EC4899]/20 to-[#A855F7]/10',
];

export function HomeScreen({ onAgentClick, onSubmitClick, onCollectionClick }: HomeScreenProps) {
  const [selectedCategory, setSelectedCategory] = useState<AgentCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredAgents, setFeaturedAgents] = useState<Agent[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [featured, cols] = await Promise.all([
          getAgents({ featured: true, limit: 5 }),
          getCollections(),
        ]);
        setFeaturedAgents(featured.agents);
        const seen = new Set<string>();
        const uniqueCollections = cols.collections.filter(c => {
          if (seen.has(c.slug)) return false;
          seen.add(c.slug);
          return true;
        });
        setCollections(uniqueCollections);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
    fetchData();
  }, []);

  const handleClearSearch = useCallback(() => setSearchQuery(''), []);
  void onSubmitClick;

  const isSearching = searchQuery.trim().length > 0;

  return (
    <div className="space-y-4 pb-24 animate-screen-fade">
      {/* Title */}
      <h1 className="text-2xl font-bold text-white">Discover Agents</h1>

      {/* Search bar */}
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search AI agents..."
      />

      {/* Category pills */}
      {!isSearching && (
        <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
          <div className="flex gap-2 pb-1">
            {CATEGORIES.map((cat) => {
              const isSelected = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  onClick={() => {
                    hapticFeedback('light');
                    setSelectedCategory(cat.value);
                  }}
                  className={`
                    flex-shrink-0 px-4 h-9 rounded-full text-sm font-medium whitespace-nowrap
                    transition-all duration-200
                    focus-visible:ring-2 focus-visible:ring-[#0098EA] outline-none
                    ${isSelected
                      ? 'bg-[#0098EA] text-white hover:bg-[#0098EA]/90'
                      : 'bg-[#242424] text-white border border-white/5 hover:bg-white/5'
                    }
                  `}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <HomeSkeleton />
      ) : (
        <>
          {/* Featured hero */}
          {featuredAgents.length > 0 && !isSearching && selectedCategory === 'all' && (
            <FeaturedHero agent={featuredAgents[0]} onClick={onAgentClick} />
          )}

          {/* Collections */}
          {collections.length > 0 && !isSearching && selectedCategory === 'all' && (
            <div>
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <span>📂</span><span>Collections</span>
              </h2>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                {collections.map((col, i) => (
                  <button
                    key={col.id}
                    onClick={() => onCollectionClick?.(col.slug)}
                    className={`flex-shrink-0 w-28 p-3 rounded-xl bg-gradient-to-br ${COLLECTION_GRADIENTS[i % COLLECTION_GRADIENTS.length]}
                      bg-[#242424] border border-white/5
                      hover:scale-[1.04] active:scale-[0.97] transition-all duration-200 text-left
                      focus-visible:ring-2 focus-visible:ring-[#0098EA] outline-none`}
                  >
                    <p className="text-2xl mb-1">{col.emoji}</p>
                    <p className="text-xs font-medium text-white truncate">{col.name}</p>
                    <p className="text-[11px] text-[#a0a0a0]">{col.agent_count || 0} apps</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* App list */}
          <div>
            {!isSearching && (
              <h2 className="text-lg font-bold text-white mb-3">
                {selectedCategory === 'all' ? 'All Apps' : CATEGORIES.find(c => c.value === selectedCategory)?.label || 'Apps'}
              </h2>
            )}
            <AgentList
              category={selectedCategory}
              searchQuery={searchQuery}
              onAgentSelect={onAgentClick}
              onSubmit={onSubmitClick}
              onClearSearch={handleClearSearch}
            />
          </div>
        </>
      )}
    </div>
  );
}
